/**
 * Recommended daily intake for the goal, using the trajectory engine's own model (Hall 2008 energy
 * density, the Forbes lean/fat split, and expenditure that falls as weight falls; see
 * backend/worker/src/trajectory/model.ts). One constant daily intake that lands on the target.
 *
 * With a target date the intake is solved for that date. Without one it comes from a weekly pace:
 *  - losing: 0.5% of body weight a week by default. Garthe I et al. Int J Sport Nutr Exerc Metab.
 *    2011;21(2):97-104: athletes losing ~0.7%/week kept more lean mass and strength than at
 *    ~1.4%/week. Helms ER et al. J Int Soc Sports Nutr. 2014;11:20: 0.5-1.0% a week.
 *  - gaining: 0.25% a week by default. Iraki J et al. Sports (Basel). 2019;7(7):154:
 *    0.25-0.5% of body weight a week off-season.
 */
import {
  DEFAULT_EXPENDITURE_SLOPE, energyDensity, simulate, solveIntakeForBodyFat, solveIntakeForTarget, weightAtBodyFatKeepingLean,
} from '@engine/trajectory';
import { addDays, daysBetween } from './dates';

export const PACE_OPTIONS: ReadonlyArray<{ pct: number; label: string }> = [
  { pct: -1, label: 'Fast: lose 1% of body weight a week' },
  { pct: -0.75, label: 'Lose 0.75% a week' },
  { pct: -0.5, label: 'Steady: lose 0.5% a week (recommended)' },
  { pct: -0.25, label: 'Gentle: lose 0.25% a week' },
  { pct: 0, label: 'Maintain' },
  { pct: 0.25, label: 'Lean gain: 0.25% a week (recommended)' },
  { pct: 0.5, label: 'Gain 0.5% a week' },
];

export const DEFAULT_LOSS_PACE: number = -0.5;
export const DEFAULT_GAIN_PACE: number = 0.25;
const HORIZON_DAYS: number = 1095;

export interface GoalPlanInput {
  today: string;
  weightKg: number | null;
  bodyFatPct: number | null;
  /** A measured lean mass, used for the fat mass when no body-fat % is known. */
  leanMassKg: number | null;
  targetWeightKg: number | null;
  targetBodyFatPct: number | null;
  targetDate: string | null;
  /** % of body weight per week, negative = loss. Ignored when there is a target date. */
  pacePct: number | null;
  /** Typical daily burn (planning figure). */
  tdeeKcal: number | null;
  bmrKcal: number | null;
}

export type GoalPlan =
  | {
    ok: true;
    dailyCalories: number;
    /** Intake minus burn: negative = deficit. */
    vsBurnKcal: number;
    weeklyChangeKg: number;
    weeklyChangePct: number;
    /** The weight the plan aims at (for a body-fat goal: where it gets there). */
    targetWeightKg: number;
    endBodyFatPct: number | null;
    reachDate: string | null;
    basis: 'date' | 'pace' | 'maintain';
    solvedFor: 'weight' | 'body_fat' | 'maintain';
    warnings: string[];
  }
  | { ok: false; reason: string };

const pctOf = (fat: number | null, w: number): number | null => (fat === null ? null : (100 * fat) / w);

export function planGoal(i: GoalPlanInput): GoalPlan {
  if (i.weightKg === null) return { ok: false, reason: 'Log a weigh-in first; the plan starts from your current weight.' };
  if (i.tdeeKcal === null || !(i.tdeeKcal > 0)) return { ok: false, reason: 'Your daily burn isn’t known yet. Fill in About you and Your daily burn.' };
  const w: number = i.weightKg;
  const tdee: number = i.tdeeKcal;
  const fatMass: number | null = i.bodyFatPct !== null ? (w * i.bodyFatPct) / 100
    : i.leanMassKg !== null && i.leanMassKg < w ? w - i.leanMassKg : null;
  const bfNow: number | null = pctOf(fatMass, w);
  const start = { weightKg: w, fatMassKg: fatMass };
  const slope: number = DEFAULT_EXPENDITURE_SLOPE;

  if (i.targetWeightKg === null && i.targetBodyFatPct === null) {
    if (i.pacePct === null || i.pacePct === 0) {
      return { ok: true, dailyCalories: Math.round(tdee), vsBurnKcal: 0, weeklyChangeKg: 0, weeklyChangePct: 0, targetWeightKg: w, endBodyFatPct: bfNow, reachDate: null, basis: 'maintain', solvedFor: 'maintain', warnings: [] };
    }
    return { ok: false, reason: 'Set a target weight or a target body fat %.' };
  }
  if (i.targetWeightKg === null && fatMass === null) {
    return { ok: false, reason: 'To plan a body-fat goal, add your current body fat (Body composition).' };
  }
  const byBodyFat: boolean = i.targetWeightKg === null;
  const tw: number = byBodyFat ? weightAtBodyFatKeepingLean(w, bfNow as number, i.targetBodyFatPct as number) : (i.targetWeightKg as number);
  const warnings: string[] = [];
  const losing: boolean = tw < w;
  let intake: number;
  let reachDate: string | null;
  let basis: 'date' | 'pace';
  let pace: number | null = null;

  if (i.targetDate) {
    const days: number = daysBetween(i.today, i.targetDate);
    if (days < 7) return { ok: false, reason: 'Pick a target date at least a week away.' };
    if (days > HORIZON_DAYS) return { ok: false, reason: 'Pick a target date within 3 years.' };
    intake = byBodyFat
      ? solveIntakeForBodyFat(start, tdee, i.targetBodyFatPct as number, days, slope) as number
      : solveIntakeForTarget(start, tdee, tw, days, slope);
    reachDate = i.targetDate;
    basis = 'date';
  } else {
    pace = i.pacePct ?? (losing ? DEFAULT_LOSS_PACE : DEFAULT_GAIN_PACE);
    if (Math.abs(tw - w) < 0.05) return { ok: true, dailyCalories: Math.round(tdee), vsBurnKcal: 0, weeklyChangeKg: 0, weeklyChangePct: 0, targetWeightKg: w, endBodyFatPct: bfNow, reachDate: i.today, basis: 'maintain', solvedFor: 'maintain', warnings: [] };
    if (pace === 0) return { ok: false, reason: 'Pick how fast you want to get there, or a target date.' };
    if (losing && pace > 0) return { ok: false, reason: 'Your target is below your current weight, so pick a loss pace.' };
    if (!losing && pace < 0) return { ok: false, reason: 'Your target is above your current weight, so pick a gain pace.' };
    intake = tdee + (((w * pace) / 100) / 7) * energyDensity(fatMass);
    const sim = simulate(start, tdee, intake, HORIZON_DAYS, slope);
    reachDate = null;
    const target: number = byBodyFat ? (i.targetBodyFatPct as number) : tw;
    for (let d = 1; d <= HORIZON_DAYS; d++) {
      const s = sim[d]!;
      const v: number | null = byBodyFat ? pctOf(s.fatMassKg, s.weightKg) : s.weightKg;
      if (v !== null && (losing ? v <= target : v >= target)) { reachDate = addDays(i.today, d); break; }
    }
    if (reachDate === null) warnings.push('At this pace the target is more than 3 years away, because the burn drops as weight drops. Pick a faster pace or a date.');
    basis = 'pace';
  }

  // Where the plan lands: the target weight, or for a body-fat goal the weight at which it gets there.
  let endWeight: number = tw;
  let endBodyFatPct: number | null = null;
  let spanDays: number = 7;
  if (reachDate !== null) {
    spanDays = Math.max(1, daysBetween(i.today, reachDate));
    const endState = simulate(start, tdee, intake, spanDays, slope)[spanDays]!;
    if (byBodyFat) endWeight = endState.weightKg;
    endBodyFatPct = pctOf(endState.fatMassKg, endState.weightKg);
  }
  const weeklyChangeKg: number = pace !== null ? (w * pace) / 100 : ((endWeight - w) / spanDays) * 7;
  const weeklyChangePct: number = (100 * weeklyChangeKg) / w;

  if (weeklyChangePct < -1) warnings.push(`That's about ${Math.abs(weeklyChangePct).toFixed(1)}% of body weight a week. Above about 1% a week more of the loss tends to come from muscle (Garthe 2011; Helms 2014).`);
  if (weeklyChangePct > 0.5) warnings.push(`That's about ${weeklyChangePct.toFixed(1)}% of body weight a week. Gaining faster than about 0.5% a week mostly adds fat (Iraki 2019).`);
  if (i.bmrKcal !== null && intake < i.bmrKcal) warnings.push(`This is below your resting burn (${Math.round(i.bmrKcal).toLocaleString()} kcal). Eating under your BMR for long stretches is hard to sustain; consider a later date or a slower pace.`);
  if (intake < 1200) warnings.push('Under 1,200 kcal a day is very low; get medical supervision for intakes this low.');

  return {
    ok: true,
    dailyCalories: Math.round(intake),
    vsBurnKcal: Math.round(intake - tdee),
    weeklyChangeKg,
    weeklyChangePct,
    targetWeightKg: endWeight,
    endBodyFatPct,
    reachDate,
    basis,
    solvedFor: byBodyFat ? 'body_fat' : 'weight',
    warnings,
  };
}
